import { IAcquirerOnboarding } from '@/@types/AcquirerOnboarding';
import { toast } from '@/hooks/use-toast';
import { OnboardingService } from '@/services/onboarding.service';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useState } from 'react';

/**
 * Estado do onboarding hospedado das adquirentes do vendedor.
 *
 * O link nunca é enviado por e-mail ou mensagem: é aberto daqui, do painel autenticado. Como pode
 * ter expirado ou já ter sido consumido, o botão sempre regenera antes de abrir em vez de reusar
 * o `hosted_url` que veio na listagem.
 */
export function useAcquirerOnboarding() {
  const queryClient = useQueryClient();
  const [openingFor, setOpeningFor] = useState<string | null>(null);

  const {
    data: onboardings = [],
    isPending,
    isError,
    error,
  } = useQuery({
    queryKey: ['acquirer-onboardings'],
    queryFn: async () => await OnboardingService.getAcquirerOnboardings(),
    // Estado muda por webhook do provedor, não por ação nossa: revalida ao voltar pra aba.
    refetchOnWindowFocus: true,
  });

  const { mutateAsync: submitFields, isPending: isSubmitting } = useMutation({
    mutationFn: async ({
      acquirer,
      values,
    }: {
      acquirer: string;
      values: Record<string, unknown>;
    }) => await OnboardingService.submitAcquirerOnboardingFields(acquirer, values),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['acquirer-onboardings'] });
      // O banner do layout lê has_pending_acquirer_onboarding do /me, que fica cacheado por 24h.
      // Sem invalidar aqui, ele continuaria avisando de algo que o vendedor acabou de resolver.
      queryClient.invalidateQueries({ queryKey: ['user-info'] });

      // O envio pode dar certo e a abertura do recebedor não: dizer "está liberado" nesse caso
      // seria mentira, e o vendedor ficaria procurando um botão que não existe.
      if (updated.hosted_url) {
        toast({
          description:
            'Dados enviados. Confira o que ter em mãos e abra a verificação quando estiver pronto.',
        });

        return;
      }

      if (Object.keys(updated.field_errors ?? {}).length > 0) {
        toast({
          variant: 'destructive',
          description:
            'Alguns dados não foram aceitos. Revise os campos marcados e envie novamente.',
        });

        return;
      }

      toast({
        description:
          'Recebemos seus dados. Estamos concluindo a ativação da sua conta de recebimento.',
      });
    },
    onError: (error) => {
      toast({ variant: 'destructive', description: extractMessage(error) });
    },
  });

  const { mutateAsync: refreshLink } = useMutation({
    mutationFn: async (acquirer: string) =>
      await OnboardingService.refreshAcquirerOnboardingLink(acquirer),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['acquirer-onboardings'] });
      queryClient.invalidateQueries({ queryKey: ['user-info'] });
    },
  });

  /**
   * Abre a janela ANTES do await: o navegador bloqueia window.open que não nasce de um clique.
   *
   * Sem `noopener` de propósito — com essa flag o window.open retorna null por especificação
   * (ela existe justamente para cortar a referência), e aí a aba abria em branco enquanto o
   * fallback redirecionava a página atual. A referência é cortada depois, via opener = null.
   */
  const openHostedForm = async (onboarding: IAcquirerOnboarding) => {
    setOpeningFor(onboarding.acquirer);

    const popup = window.open('about:blank', '_blank');

    try {
      const refreshed = await refreshLink(onboarding.acquirer);

      if (!refreshed.hosted_url) {
        throw new Error('Link de verificação indisponível.');
      }

      if (popup && !popup.closed) {
        popup.location.href = refreshed.hosted_url;
        // Corta a referência agora que a navegação foi feita.
        popup.opener = null;

        return;
      }

      // Popup bloqueado pelo navegador: navega na própria aba, sem deixar aba órfã.
      window.location.href = refreshed.hosted_url;
    } catch (error) {
      popup?.close();

      toast({ variant: 'destructive', description: extractMessage(error) });
    } finally {
      setOpeningFor(null);
    }
  };

  return {
    onboardings,
    isPending,
    isError,
    errorMessage: isError ? extractMessage(error) : null,
    openHostedForm,
    openingFor,
    submitFields,
    isSubmitting,
  };
}

function extractMessage(error: unknown): string {
  if (isAxiosError(error)) {
    return error.response?.data?.message ?? error.message;
  }

  return error instanceof Error
    ? error.message
    : 'Não foi possível concluir a operação.';
}
